import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { formatDuration } from '@chronos/shared';
import { useAuth } from '@/auth/AuthContext';
import { useCurrentEntry, useTimerActions } from '@/hooks/queries';
import { Dot, Spinner } from './ui';
import { isDesktopApp } from '@/lib/desktop';
import { useElapsed } from '@/lib/elapsed';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  roles?: ('ADMIN' | 'MANAGER')[];
}

const NAV: { section: string; items: NavItem[] }[] = [
  {
    section: 'Track',
    items: [
      { to: '/', label: 'Timer', icon: '⏱' },
      { to: '/timesheet', label: 'My timesheet', icon: '📅' },
      { to: '/my-activity', label: 'My activity', icon: '🖼' },
      { to: '/download', label: 'Desktop app', icon: '💻' },
      { to: '/profile', label: 'My profile', icon: '🪪' },
    ],
  },
  {
    section: 'Attendance',
    items: [
      { to: '/attendance', label: 'My attendance', icon: '🗓' },
      { to: '/leave', label: 'Apply for leave', icon: '✈️' },
      { to: '/holidays', label: 'Holidays', icon: '🎉' },
      { to: '/handbook', label: 'HR Handbook', icon: '📖' },
      { to: '/attendance/team', label: 'Team attendance', icon: '👥', roles: ['ADMIN', 'MANAGER'] },
      { to: '/leave/requests', label: 'Leave requests', icon: '📨', roles: ['ADMIN', 'MANAGER'] },
    ],
  },
  {
    section: 'Salary',
    items: [
      { to: '/salary', label: 'Salary sheet', icon: '💸', roles: ['ADMIN'] },
      { to: '/salary/leave-bank', label: 'Leave bank', icon: '🏦', roles: ['ADMIN'] },
      { to: '/salary/settings', label: 'Salary settings', icon: '🧮', roles: ['ADMIN'] },
    ],
  },
  {
    section: 'Manage',
    items: [
      { to: '/admin', label: 'Dashboard', icon: '📊', roles: ['ADMIN', 'MANAGER'] },
      { to: '/admin/reports', label: 'Reports', icon: '📈', roles: ['ADMIN', 'MANAGER'] },
      { to: '/admin/screenshots', label: 'Screenshots', icon: '🖥', roles: ['ADMIN', 'MANAGER'] },
      { to: '/admin/users', label: 'Users', icon: '👥', roles: ['ADMIN'] },
      { to: '/admin/projects', label: 'Projects', icon: '📁', roles: ['ADMIN', 'MANAGER'] },
      { to: '/admin/settings', label: 'Settings', icon: '⚙️', roles: ['ADMIN'] },
      { to: '/admin/audit', label: 'Audit log', icon: '🧾', roles: ['ADMIN'] },
    ],
  },
];

function RunningTimer() {
  const { data: entry } = useCurrentEntry();
  const { stop } = useTimerActions();
  const seconds = useElapsed(entry);

  if (!entry) return null;

  return (
    <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5">
      <span className="relative flex h-2 w-2" aria-hidden="true">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
      </span>
      <div className="min-w-0">
        <p className="truncate text-xs font-medium text-emerald-900">{entry.projectName}</p>
        <p className="font-mono text-sm font-semibold tabular-nums text-emerald-800">{formatDuration(seconds)}</p>
      </div>
      <button className="btn-secondary py-1 text-xs" onClick={() => stop.mutate(entry.id)} disabled={stop.isPending}>
        {stop.isPending ? <Spinner className="h-3 w-3" /> : 'Stop'}
      </button>
    </div>
  );
}

export function Layout() {
  const { user, logout, hasRole } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  const visible = NAV.map((group) => ({
    ...group,
    items: group.items.filter((i) => !i.roles || hasRole(...i.roles)),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="flex h-full">
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-60 shrink-0 border-r border-slate-200 bg-white transition-transform md:static md:translate-x-0 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-14 items-center gap-2 border-b border-slate-200 px-4">
          <span className="grid h-7 w-7 place-items-center rounded-md bg-brand-600 text-sm font-bold text-white">T</span>
          <span className="font-semibold tracking-tight">TickTock</span>
          {isDesktopApp() && <span className="badge bg-slate-100 text-slate-600">desktop</span>}
        </div>

        <nav className="flex flex-col gap-5 overflow-y-auto p-3">
          {visible.map((group) => (
            <div key={group.section}>
              <p className="px-2 pb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">{group.section}</p>
              <ul className="space-y-0.5">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.to === '/' || item.to === '/admin'}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition ${
                          isActive ? 'bg-brand-50 font-medium text-brand-700' : 'text-slate-600 hover:bg-slate-100'
                        }`
                      }
                    >
                      <span aria-hidden="true">{item.icon}</span>
                      {item.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      {mobileOpen && <div className="fixed inset-0 z-30 bg-slate-900/30 md:hidden" onClick={() => setMobileOpen(false)} aria-hidden="true" />}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-14 items-center justify-between gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur">
          <button className="btn-ghost px-2 md:hidden" onClick={() => setMobileOpen((v) => !v)} aria-label="Toggle navigation">
            ☰
          </button>
          <div className="flex-1" />
          <RunningTimer />
          <div className="flex items-center gap-2 border-l border-slate-200 pl-3">
            <div className="hidden text-right sm:block">
              <p className="text-sm font-medium leading-tight text-slate-800">{user?.name}</p>
              <p className="flex items-center justify-end gap-1 text-xs text-slate-500">
                {user?.roles.map((r) => (
                  <span key={r} className="badge bg-slate-100 text-slate-600">
                    {r.toLowerCase()}
                  </span>
                ))}
              </p>
            </div>
            <button className="btn-secondary py-1.5 text-xs" onClick={() => void logout()}>
              Sign out
            </button>
          </div>
        </header>

        <main className="min-w-0 flex-1 overflow-y-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export { Dot };
